import { X } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function EditMedicationModal({ medication, onClose, onSave, onDelete }) {
    const [form, setForm] = useState({ ...medication });
    const [reminderTimes, setReminderTimes] = useState(() => {
        if (medication?.times) {
            try {
                return Array.isArray(medication.times)
                    ? medication.times
                    : JSON.parse(medication.times);
            } catch {
                return [];
            }
        }
        return [];
    });

    useEffect(() => {
        setForm({ ...medication });
    }, [medication]);

    const toMySQLDate = (input) => input.slice(0, 10);

    const handleChange = (e) => {
        setForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleTimeChange = (index, value) => {
        const updated = [...reminderTimes];
        updated[index] = value;
        setReminderTimes(updated);
    };

    const addTimeInput = () => {
        setReminderTimes([...reminderTimes, '']);
    };

    const removeTimeInput = (index) => {
        setReminderTimes(reminderTimes.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanedTimes = reminderTimes
            .map((t) => t.trim())
            .filter((t) => t);
        const startDateStr = form.start_date ? toMySQLDate(form.start_date) : null;
        const endDateStr = form.end_date ? toMySQLDate(form.end_date) : null;

        const updatedMedication = {
            ...form,
            start_date: startDateStr,
            end_date: endDateStr,
            times: cleanedTimes.length > 0 ? JSON.stringify(cleanedTimes) : null,
        };

        await onSave(medication.id, updatedMedication);
        onClose();
    };

    const handleDelete = async () => {
        const confirmed = window.confirm("Are you sure you want to delete this medication?");
        if (!confirmed) return;

        await onDelete(medication.id);
        onClose(); // close modal after deletion
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div className="relative bg-white p-6 rounded-lg shadow-lg w-full max-w-xl">
                <h2 className="text-xl font-bold mb-4">Edit Medication</h2>
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <X size={20} />
                </button>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Drug Name</label>
                        <input
                            type="text"
                            name="drug_name"
                            value={form.drug_name}
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                            required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Dosage</label>
                        <input
                            type="text"
                            name="dosage"
                            value={form.dosage}
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                        />
                    </div>

                    <div className="flex flex-col md:col-span-2">
                        <label className="mb-3 text-sm font-medium">Description (optional)</label>
                        <textarea
                            name="description"
                            rows="2"
                            value={form.description}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        ></textarea>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Prescribing Doctor</label>
                        <input
                            type="text"
                            name="prescribing_doctor"
                            value={form.prescribing_doctor}
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Condition Treated</label>
                        <input
                            type="text"
                            name="condition_treated"
                            value={form.condition_treated}
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                        />
                    </div>

                    <div className="flex flex-col col-span-full">
                        <label className="text-sm font-medium mb-1">Instructions (optional)</label>
                        <input
                            type="text"
                            name="instructions"
                            value={form.instructions}
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Start Date</label>
                        <input
                            type="date"
                            name="start_date"
                            value={
                                form.start_date
                                    ? new Date(form.start_date).toISOString().slice(0, 10)
                                    : ""
                            }
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">End Date (optional)</label>
                        <input
                            type="date"
                            name="end_date"
                            value={
                                form.end_date
                                    ? new Date(form.end_date).toISOString().slice(0, 10)
                                    : ""
                            }
                            onChange={handleChange}
                            className="px-3 py-2 border rounded"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block font-medium text-sm mb-1">Daily Reminder Times (optional)</label>
                        {reminderTimes.map((time, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-2">
                                <input
                                    type="time"
                                    value={time}
                                    onChange={(e) => handleTimeChange(idx, e.target.value)}
                                    className="border rounded px-2 py-1"
                                    required
                                />
                                {reminderTimes.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeTimeInput(idx)}
                                        className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                        aria-label="Remove reminder time"
                                    >
                                        x
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addTimeInput}
                            className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                            + Add time
                        </button>
                    </div>
                    <div className="col-span-full flex justify-between items-center mt-4">
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                            Delete
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Save
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}

